import { service } from '@ember/service';
import type Intl from 'ember-intl/services/intl';
import type { PslfForm } from './pslf-form.type.ts';
import {
  SUBMITTED_STATUSES,
  PENDING_SUBMIT_STATUSES,
  PENDING_SIGNATURE_STATUSES,
  ACTIVE_STATUSES,
  INACTIVE_STATUSES,
  ONE_YEAR_DAYS,
  RECERT_WARNING_DAYS,
} from './pslf-form.constants';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
export interface PslfFormExtension extends PslfForm {}
export class PslfFormExtension {
  @service declare intl: Intl;

  /**************************
   **  Computed Properties **
   **************************/
  get isPriorEmployer() {
    return this.formType === 'priorEmployer';
  }

  get isCurrentEmployer() {
    return this.formType === 'currentEmployer';
  }

  get isNonPartnerApproverForm() {
    return this.isNonPartner;
  }

  get isPriorPeriodForm() {
    return this.isPriorEmployer && !this.isNonPartnerApproverForm;
  }

  /**
   * Returns a translation-key suffix that distinguishes between
   * prior-period forms at the current employer vs truly-different
   * former employer forms.
   */
  get formTypeDisplayKey(): string {
    if (this.isPriorPeriodForm) return 'currentEmployerPriorPeriod';
    return this.formType;
  }

  get isOboForm() {
    return !!this.submittedOnBehalfOfById;
  }

  get isPriorEmployerClosed() {
    return !!this.borrowerFields?.['borrower-prior-employer-closed'];
  }

  get isPrintAndMail() {
    return !!this.borrowerFields?.['borrower-print-and-mail'];
  }

  get employerDisplayName() {
    return (
      this.borrowerFields?.['borrower-employer-name'] ||
      this.employerFields?.['employer-name'] ||
      ''
    );
  }

  get fullEmployerAddress() {
    const line1 = this.employerFields?.['employer-street-address'] || '';
    const line2 = this.employerFields?.['employer-apartment'] || '';
    const city = this.employerFields?.['employer-city'] || '';
    const state = this.employerFields?.['employer-state'] || '';
    const zip = this.employerFields?.['employer-zip'] || '';

    // Build street portion (line1 and line2)
    const streetParts = [line1, line2].filter(Boolean);
    const streetAddress = streetParts.join(' ');

    // Build city/state/zip portion
    const cityStateZipParts = [];
    if (city) {
      cityStateZipParts.push(city);
    }
    // Combine state and zip
    const stateZip = [state, zip].filter(Boolean).join(' ');
    if (stateZip) {
      cityStateZipParts.push(stateZip);
    }
    const cityStateZip = cityStateZipParts.join(', ');

    // Combine all parts
    const allParts = [streetAddress, cityStateZip].filter(Boolean);
    return allParts.join(', ');
  }

  // TODO: Type employer fields so inference will work.
  get approverDisplayEmail() {
    if (this.isPrintAndMail) {
      return `${this.intl.t('pslf.table.not_applicable')} - ${this.intl.t(
        'pslf.table.print_and_mail'
      )}`;
    }
    if (this.isPriorEmployerClosed) {
      return `${this.intl.t('pslf.table.not_applicable')} - ${this.intl.t(
        'pslf.table.prior_employer_closed'
      )}`;
    }
    return (
      this.employerFields?.['employer-email'] ||
      this.employerFields?.['employer-contact-email'] ||
      ''
    );
  }

  get isAwaitingSignature() {
    return ['created', 'borrower_signed'].includes(this.status);
  }

  get needsReview() {
    return this.status === 'borrower_signed' && !this.isNonPartner;
  }

  get isEmployerSignLinkValid() {
    if (this.isPriorEmployerClosed || this.isPrintAndMail) {
      return false;
    }
    return this.isAwaitingSignature;
  }

  get hasPdfPreview() {
    return this.attachments.length > 0;
  }

  get canTransferPdf() {
    return !!this.pslfTemplateId && this.status !== 'created';
  }

  get canFax() {
    return [
      'awaiting_submit',
      'submit_errored',
      'submitted',
      're_submitted',
      're_submit_errored',
    ].includes(this.status);
  }

  get canCancel() {
    return ['borrower_signed', 'employer_signed', 'awaiting_submit', 'submit_errored'].includes(
      this.status
    );
  }

  get canChangeApprover() {
    if (this.isPriorEmployerClosed || this.isPrintAndMail) {
      return false;
    }

    return ['borrower_signed'].includes(this.status) && this.isNonPartnerApproverForm;
  }

  get canResendEmail() {
    return (
      ['borrower_signed'].includes(this.status) &&
      !this.isPrintAndMail &&
      !this.isPriorEmployerClosed
    );
  }

  // ------------------------------------------------------------------
  // Dashboard status predicates (v2 PSLF dashboard)
  // ------------------------------------------------------------------
  get isSubmitted() {
    return (SUBMITTED_STATUSES as readonly string[]).includes(this.status);
  }

  get isPendingSubmit() {
    return (PENDING_SUBMIT_STATUSES as readonly string[]).includes(this.status);
  }

  get isSubmittedOrPending() {
    return this.isSubmitted || this.isPendingSubmit;
  }

  get isPendingSignature() {
    return (PENDING_SIGNATURE_STATUSES as readonly string[]).includes(this.status);
  }

  get isActiveStatus() {
    return (ACTIVE_STATUSES as readonly string[]).includes(this.status);
  }

  get isInactiveStatus() {
    return (INACTIVE_STATUSES as readonly string[]).includes(this.status);
  }

  // ------------------------------------------------------------------
  // Time helpers
  // ------------------------------------------------------------------
  /**
   * Days since the form was submitted. Falls back to createdAt when no
   * submittedAt is present. Returns 0 when neither is available.
   */
  get daysSinceSubmitted() {
    const referenceDate = this.submittedAt || this.createdAt;
    if (!referenceDate) return 0;
    return (Date.now() - new Date(referenceDate).getTime()) / MS_PER_DAY;
  }

  /** True when the form was submitted within the last ~12 months. */
  get isRecentlySubmitted() {
    if (!this.isSubmitted) return false;
    return this.daysSinceSubmitted < ONE_YEAR_DAYS;
  }

  /** True when a submitted form is 11+ months old and should prompt recert. */
  get isExpiring() {
    if (!this.isSubmittedOrPending) return false;
    return this.daysSinceSubmitted >= RECERT_WARNING_DAYS;
  }

  // ------------------------------------------------------------------
  // Field extraction (JSONB key coupling lives here and only here)
  // ------------------------------------------------------------------
  get employmentStartDate() {
    return (
      this.borrowerFields?.['borrower-employment-start-date'] ||
      this.employerFields?.['employment-start-date'] ||
      null
    );
  }

  get employmentEndDate() {
    return (
      this.borrowerFields?.['borrower-employment-end-date'] ||
      this.employerFields?.['employment-end-date'] ||
      null
    );
  }

  /**
   * Pick the most meaningful timestamp on a form for user-facing display,
   * along with the intl key that describes it.
   */
  get mostRelevantDate() {
    if (this.submittedAt) {
      return { date: this.submittedAt, key: 'pslf.reimagined.form_status.submitted_on' };
    }
    if (this.employerCompletedAt) {
      return { date: this.employerCompletedAt, key: 'pslf.reimagined.form_status.signed_on' };
    }
    if (this.borrowerCompletedAt) {
      return { date: this.borrowerCompletedAt, key: 'pslf.reimagined.form_status.signed_on' };
    }
    if (this.createdAt) {
      return { date: this.createdAt, key: 'pslf.reimagined.form_status.created_on' };
    }
    return null;
  }
}

const Registration = {
  kind: 'object',
  name: 'pslf-form',
  features: PslfFormExtension,
};
export default Registration;
